import React, { type RefObject } from 'react';
import { useMachine } from '@xstate/react';
import { injectIntl } from 'react-intl';
import { withRouter, type RouteComponentProps } from 'react-router-dom';
import { type PMCConfig } from '@kyruus/types';
import type { WrappedComponentProps, MessageDescriptor } from '@kyruus/intl';
import { Tabs } from '@kyruus/tab';
import { Search } from '@kyruus/search-sdk';

import messages from './messages';
import {
  useLocationSections,
  LOCATION_SECTION_IDS,
  LOCATION_SECTION_TITLE_IDS
} from './hooks/useLocationSections';

import type {
  MarketableLocation,
  Log,
  SearchQuery,
  AppSettings,
  SearchSummary,
  ExtendedProvider,
  AvailabilityProps
} from 'Src/types';
import {
  createLocationV9Machine,
  STATES,
  type LocationResult,
  type ProvidersResult
} from 'Src/machines/location-v9';
import PoweredByLogo from 'Src/shared/powered-by-logo';
import Error from 'Src/error';

import { Meta } from './Meta';
import { Summary } from './Summary';
import { About } from './About';
import { Location } from './Location';
import { LocationPage, LocationNavContainer, SectionContainer } from './styles';
import { List } from './List';
import { TITLE_MESSAGES, SEARCH_TITLE_MESSAGES } from './constants';
import { Providers } from './Providers';
import { trackLocationNavClick } from '../tracking/tracking-utils';
import { isCCF } from '../utils/cerner';
import { getSpecialtiesList } from './utils';

interface BaseLocationProps extends WrappedComponentProps {
  appSettings: AppSettings;
  config: PMCConfig;
  currentQuery: SearchQuery;
  customerCode: string;
  fetchLocation: () => Promise<LocationResult>;
  fetchProvidersByLocation: (
    locationId: string,
    currentQuery: SearchQuery
  ) => Promise<ProvidersResult>;
  locationId: string;
  locationObject: MarketableLocation;
  log: Log;
  globalLoading: boolean;
  globalError: any;
  productName: string;
  searchSummary: SearchSummary;
  searchSDK: RefObject<Search>;
  providers: {
    providers: ExtendedProvider[];
    totalProviders: number;
  };
}

interface OnActivateMetadata {
  name?: string;
}

type LocationV9Props = BaseLocationProps &
  RouteComponentProps &
  AvailabilityProps;

export const LocationV9 = injectIntl(
  withRouter(
    ({
      appSettings,
      config,
      currentQuery,
      customerCode,
      fetchLocation,
      fetchProvidersByLocation,
      history,
      intl,
      locationId,
      locationObject,
      globalLoading,
      globalError,
      log,
      productName,
      providers,
      searchSummary,
      appointmentInfo,
      searchSDK,
      slots,
      slotsStatus,
      purposeOptionsByProviderId,
      availabilityError,
      purposesLoading,
      fetchProviderAvailability,
      fetchPurposesByProviderId
    }: LocationV9Props) => {
      // Only show error page if there's an actual error state, not just missing data
      if (globalError && !globalLoading) {
        return (
          <Error
            config={config}
            customerCode={customerCode}
            history={history}
            log={log}
          />
        );
      }

      const [state, , service] = useMachine(
        createLocationV9Machine({
          currentQuery,
          fetchLocation,
          fetchProvidersByLocation,
          location: locationObject,
          locationId,
          providers: providers?.providers,
          totalProviders: providers?.totalProviders,
          providersPage: 1,
          searchSDK
        })
      );

      const { location: marketableLocation } = state.context;

      const { navItems, rendered } = useLocationSections({
        location: state?.context?.location,
        totalProviders: state?.context?.totalProviders
      });

      if (state.matches({ [STATES.location]: STATES.error })) {
        return (
          <Error
            config={config}
            customerCode={customerCode}
            history={history}
            log={log}
          />
        );
      }

      const getNavLabel = (message: MessageDescriptor | string) => {
        if (typeof message === 'string') {
          return message;
        }
        return intl.formatMessage(message);
      };

      const navigationTabs = navItems
        .filter(({ rendered }) => rendered === true)
        .map((navItems) => ({
          ...navItems,
          label: getNavLabel(navItems.message),
          ariaLabelledBy: `location-title ${navItems.titleId}`
        }));

      const onActivate = (
        _event:
          | React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
          | React.KeyboardEvent<HTMLDivElement>,
        _id: string,
        metadata?: OnActivateMetadata
      ) => {
        if (typeof metadata !== 'string' && metadata?.name) {
          trackLocationNavClick(log, metadata.name);
        }
      };

      const getTabControlId = (tabId: string) => {
        return `location-nav-${tabId}`;
      };

      const loading = state.matches({ [STATES.location]: STATES.pending });

      return (
        <React.Fragment>
          {!loading && marketableLocation && (
            <Meta config={config} marketableLocation={marketableLocation} />
          )}
          <LocationPage
            id={locationId}
            itemScope={true}
            itemType="http://schema.org/Place"
            aria-live="polite"
            data-testid="LocationSummary"
          >
            <Summary
              currentQuery={currentQuery}
              history={history}
              loading={loading}
              log={log}
              marketableLocation={marketableLocation}
            />

            {/* Should not render navigation in CCF */}
            {!isCCF() && !loading && marketableLocation && (
              <LocationNavContainer className="location-navigation-container">
                <Tabs
                  aria-label={intl.formatMessage(messages.locationTabAriaLabel)}
                  id="location-nav"
                  tabs={navigationTabs}
                  elevation={0}
                  onActivate={onActivate}
                  getTabControlId={getTabControlId}
                  renderAsNav={true}
                />
              </LocationNavContainer>
            )}

            {!loading && marketableLocation && (
              <>
                {rendered.aboutSection && (
                  <SectionContainer
                    id={LOCATION_SECTION_IDS.ABOUT}
                    data-testid={LOCATION_SECTION_IDS.ABOUT}
                  >
                    <About
                      marketableLocation={marketableLocation}
                      log={log}
                      titleId={LOCATION_SECTION_TITLE_IDS.ABOUT}
                    />
                  </SectionContainer>
                )}

                {rendered.locationSection && (
                  <SectionContainer
                    id={LOCATION_SECTION_IDS.LOCATION}
                    data-testid={LOCATION_SECTION_IDS.LOCATION}
                  >
                    <Location
                      marketableLocation={marketableLocation}
                      appSettings={appSettings}
                      config={config}
                      titleId={LOCATION_SECTION_TITLE_IDS.LOCATION}
                    />
                  </SectionContainer>
                )}

                {rendered.insuranceSection && (
                  <SectionContainer
                    id={LOCATION_SECTION_IDS.INSURANCES}
                    data-testid={LOCATION_SECTION_IDS.INSURANCES}
                  >
                    {marketableLocation.insurance_accepted &&
                      marketableLocation.insurance_accepted.length > 0 && (
                        <List
                          data={marketableLocation.insurance_accepted}
                          log={log}
                          id="insurances"
                          searchInputTitle={intl.formatMessage(
                            SEARCH_TITLE_MESSAGES.INSURANCES
                          )}
                          title={intl.formatMessage(TITLE_MESSAGES.INSURANCES, {
                            count: marketableLocation.insurance_accepted.length
                          })}
                          titleId={LOCATION_SECTION_TITLE_IDS.INSURANCES}
                        />
                      )}
                  </SectionContainer>
                )}
                {rendered.servicesSection && (
                  <SectionContainer
                    id={LOCATION_SECTION_IDS.SERVICES}
                    data-testid={LOCATION_SECTION_IDS.SERVICES}
                  >
                    {marketableLocation.services &&
                      marketableLocation.services.length > 0 && (
                        <List
                          data={marketableLocation.services}
                          log={log}
                          id="services"
                          searchInputTitle={intl.formatMessage(
                            SEARCH_TITLE_MESSAGES.SERVICES
                          )}
                          title={intl.formatMessage(TITLE_MESSAGES.SERVICES)}
                          titleId={LOCATION_SECTION_TITLE_IDS.SERVICES}
                        />
                      )}
                  </SectionContainer>
                )}

                {rendered.specialtiesSection && (
                  <SectionContainer
                    id={LOCATION_SECTION_IDS.SPECIALTIES}
                    data-testid={LOCATION_SECTION_IDS.SPECIALTIES}
                  >
                    {marketableLocation.specialties &&
                      marketableLocation.specialties.length > 0 && (
                        <List
                          data={getSpecialtiesList(marketableLocation)}
                          log={log}
                          id="specialties"
                          searchInputTitle={intl.formatMessage(
                            SEARCH_TITLE_MESSAGES.SPECIALTIES
                          )}
                          title={intl.formatMessage(TITLE_MESSAGES.SPECIALTIES)}
                          titleId={LOCATION_SECTION_TITLE_IDS.SPECIALTIES}
                        />
                      )}
                  </SectionContainer>
                )}

                {rendered.providersSection && (
                  <SectionContainer
                    id={LOCATION_SECTION_IDS.PROVIDERS}
                    data-testid={LOCATION_SECTION_IDS.PROVIDERS}
                  >
                    <Providers
                      config={config}
                      customerCode={customerCode}
                      locationId={locationId}
                      log={log}
                      productName={productName}
                      searchSummary={searchSummary}
                      service={service}
                      appointmentInfo={appointmentInfo}
                      slots={slots}
                      slotsStatus={slotsStatus}
                      purposeOptionsByProviderId={purposeOptionsByProviderId}
                      availabilityError={availabilityError}
                      purposesLoading={purposesLoading}
                      fetchProviderAvailability={fetchProviderAvailability}
                      fetchPurposesByProviderId={fetchPurposesByProviderId}
                      titleId={LOCATION_SECTION_TITLE_IDS.PROVIDERS}
                    />
                  </SectionContainer>
                )}
              </>
            )}
            <PoweredByLogo config={config} />
          </LocationPage>
        </React.Fragment>
      );
    }
  )
);
